/**
 * Raw Products Inspection Tab
 *
 * Tab component for inspecting theoretical consumption by raw product.
 * Shows ALL consumption events by default; rawProductId and posProductId
 * are optional filters.
 *
 * @see sprint-datagrid-migration Phase 3.4b
 */

import { useState, useMemo, useCallback } from 'react';
import { Package } from 'lucide-react';
import {
  DataGrid,
  EmptyState,
  RetentionExpiredBanner,
  textColumn,
  moneyColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { cn } from '@/lib/utils';
import { useProductSaleEvents } from '../hooks';
import type {
  ProductSaleEvent,
  PaginationInfo,
  ProductSaleEventsSortField,
} from '@bstock/shared';

interface RawProductsInspectionTabProps {
  siteId?: string;
  startDate: string;
  endDate: string;
  rawProductId?: string;
  posProductId?: string;
  search?: string;
  onReceiptClick: (receiptId: string) => void;
  showFinancials?: boolean;
  sortBy?: ProductSaleEventsSortField;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: ProductSaleEventsSortField, order: 'asc' | 'desc') => void;
}

const DEFAULT_LIMIT = 20;

const SORT_FIELD_MAP: Record<string, ProductSaleEventsSortField> = {
  receiptDate: 'receiptDate',
  orderId: 'orderId',
  quantity: 'quantity',
};

export function RawProductsInspectionTab({
  siteId,
  startDate,
  endDate,
  rawProductId,
  posProductId,
  search,
  onReceiptClick,
  showFinancials = false,
  sortBy = 'receiptDate',
  sortOrder = 'desc',
  onSort,
}: RawProductsInspectionTabProps) {
  const currency = useCurrency();
  const { formatDateShort, formatTime } = useFormatDate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const { data, isLoading } = useProductSaleEvents({
    productId: rawProductId || undefined,
    productType: 'RAW_PRODUCT',
    startDate,
    endDate,
    siteId,
    page,
    limit,
    posProductId: posProductId || undefined,
    search: search || undefined,
    sortBy,
    sortOrder,
  });

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  // ---- Sort (server-side, controlled) ------------------------------------
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(([, v]) => v === sortBy)?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!onSort) return;
      if (!next) {
        onSort('receiptDate', 'desc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort],
  );

  const columns = useMemo<DataGridColumn<ProductSaleEvent>[]>(() => {
    const cols: DataGridColumn<ProductSaleEvent>[] = [
      textColumn<ProductSaleEvent>({
        id: 'receiptDate',
        header: 'Date Ticket',
        accessor: (r) => r.receiptDate,
        cell: (r) => (
          <div>
            <div className="text-sm text-gray-900">{formatDateShort(r.receiptDate)}</div>
            <div className="text-xs text-gray-500">{formatTime(r.receiptDate)}</div>
          </div>
        ),
      }),
      textColumn<ProductSaleEvent>({
        id: 'orderId',
        header: 'Order ID',
        accessor: 'orderId',
        cell: (r) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReceiptClick(r.receiptId);
            }}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
          >
            {r.orderId}
          </button>
        ),
      }),
      textColumn<ProductSaleEvent>({
        id: 'productName',
        header: 'Produit POS (source)',
        sortable: false,
        accessor: 'productName',
      }),
      textColumn<ProductSaleEvent>({
        id: 'quantity',
        header: 'Qte consommee',
        align: 'center',
        accessor: (r) => `${r.quantity} ${r.unit ?? ''}`.trim(),
        cell: (r) => (
          <span className="text-sm text-gray-900">
            {r.quantity} {r.unit ?? ''}
          </span>
        ),
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<ProductSaleEvent>({
          id: 'theoreticalCost',
          header: 'Cout theorique',
          sortable: false,
          accessor: (r) => (r.theoreticalCost !== null ? r.theoreticalCost / 100 : null),
          cell: (r) => (
            <span
              className={cn(
                'text-sm',
                r.theoreticalCost === null ? 'text-orange-600 font-medium' : 'text-gray-900',
              )}
            >
              {r.theoreticalCost !== null
                ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(r.theoreticalCost / 100)
                : 'N/A'}
            </span>
          ),
        }),
      );
    }

    return cols;
  }, [currency, onReceiptClick, showFinancials, formatDateShort, formatTime]);

  const pagination: PaginationInfo = data?.pagination ?? {
    page: 1,
    limit,
    total: 0,
    totalPages: 0,
  };

  const items = data?.items ?? [];
  const isEmpty = !isLoading && items.length === 0;
  const retentionExpired = data?._meta?.retentionExpired ?? false;

  if (isEmpty) {
    return (
      <div className="space-y-6">
        {retentionExpired && data?._meta ? (
          <RetentionExpiredBanner retentionMonths={data._meta.retentionMonths} />
        ) : (
          <EmptyState
            icon={Package}
            title="Aucune consommation pour cette periode"
            description="Aucune consommation theorique n'a ete enregistree durant la periode selectionnee. Modifiez la date ou les filtres pour afficher des resultats."
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Retention expired: la periode selectionnee depasse (au moins en
          partie) la fenetre de retention de theoretical_consumptions
          (US-07/US-09). On laisse volontairement passer la requete plutot
          que de bloquer le datepicker: le range peut chevaucher la fenetre
          (ex: 4 derniers mois), et le datepicker est partage avec les
          onglets Tickets/Produits Finis qui, eux, ne sont jamais purges. */}
      {retentionExpired && data?._meta && (
        <RetentionExpiredBanner retentionMonths={data._meta.retentionMonths} />
      )}

      {/* Filter info */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        {data?.product ? (
          <>
            <span>Filtre par:</span>
            <span className="font-medium text-gray-900">{data.product.name}</span>
            <span className="text-gray-400">|</span>
          </>
        ) : (
          <>
            <span>Toutes les consommations</span>
            <span className="text-gray-400">|</span>
          </>
        )}
        <span>{pagination.total} consommation{pagination.total > 1 ? 's' : ''}</span>
      </div>

      <DataGrid<ProductSaleEvent>
        data={items}
        columns={columns}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        skeletonRows={8}
        onRowClick={(r) => onReceiptClick(r.receiptId)}
        currency={currency}
        sortState={sortState}
        onSortChange={handleSortChange}
        exportable={{
          fileName: { base: 'consommations_matieres' },
          sheetName: 'Consommations',
          position: 'header',
        }}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          total: pagination.total,
          totalPages: pagination.totalPages,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
        emptyState={
          <div className="py-8 text-center text-sm text-neutral-500">
            Aucune consommation
          </div>
        }
      />
    </div>
  );
}
